import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { usePermission } from "../hooks/usePermission";
import config from "../config/config";
import { useToast } from "../context/ToastContext";
import { useTheme, useMediaQuery } from "@mui/material";
import {
    startOfWeek,
    endOfWeek,
    startOfToday,
    endOfToday,
    startOfMonth,
    endOfMonth,
} from "date-fns";

const useOrderLogic = () => {
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [searchOrderCode, setSearchOrderCode] = useState("");
    const [filterProduct, setFilterProduct] = useState("");
    const [filterStartDate, setFilterStartDate] = useState(startOfToday().toISOString());
    const [filterEndDate, setFilterEndDate] = useState(endOfToday().toISOString());
    const [filterCustomer, setFilterCustomer] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalOrders, setTotalOrders] = useState(0);
    const [isAddOrderDialogOpen, setIsAddOrderDialogOpen] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedOrderForInvoice, setSelectedOrderForInvoice] =
        useState(null);
    const { token } = useAuth();
    const { hasPermission } = usePermission();
    const { showToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [anchorEl, setAnchorEl] = useState(null);
    const openPopover = Boolean(anchorEl);
    const id = openPopover ? "mobile-filter-popover" : undefined;

    const onOpenFilters = (e) => setAnchorEl(e.currentTarget);
    const onCloseFilters = () => setAnchorEl(null);

    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [dialogActionType, setDialogActionType] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [customerPage, setCustomerPage] = useState(1);
    const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
    const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
    const [customerSearch, setCustomerSearch] = useState("");

    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const statusOptions = [
        { value: "", label: "Tất cả" },
        { value: "Created", label: "Đã tạo" },
        { value: "Cancelled", label: "Đã hủy" },
    ];

    const fetchOrders = async (append = false) => {
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                orderCode: searchOrderCode || undefined,
                productId: filterProduct || undefined,
                startDate: filterStartDate || undefined,
                endDate: filterEndDate || undefined,
                customerId: filterCustomer || undefined,
                status: filterStatus || undefined,
            };

            const res = await axios.get(`${config.backendUrl}/api/orders`, {
                headers: { "x-auth-token": token },
                params,
            });

            const newOrders = res.data.docs || [];
            const total = res.data.totalDocs || 0;

            setOrders((prev) => (append ? [...prev, ...newOrders] : newOrders));
            setTotalOrders(total);
            setHasMore((page + 1) * rowsPerPage < total);
            setIsLoadingMore(false);
        } catch (err) {
            console.error(err);
            setOrders([]);
            setTotalOrders(0);
            setIsLoadingMore(false);
            setHasMore(false);
        }
    };

    const fetchProductsForOrder = async () => {
        try {
            const res = await axios.get(`${config.backendUrl}/api/products`, {
                headers: { "x-auth-token": token },
            });
            setProducts(res.data.docs || []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchCustomers = async (page, search) => {
        if (isFetchingCustomers) return;
        setIsFetchingCustomers(true);
        try {
            const res = await axios.get(`${config.backendUrl}/api/customers`, {
                headers: { "x-auth-token": token },
                params: { page, limit: 10, search },
            });
            const newCustomers = res.data.docs || [];
            setCustomers((prev) =>
                page === 1 ? newCustomers : [...prev, ...newCustomers]
            );
            setHasMoreCustomers(res.data.hasNextPage);
            setCustomerPage(page + 1);
        } catch (err) {
            console.error(err);
        } finally {
            setIsFetchingCustomers(false);
        }
    };

    const handleOrderAdded = (newOrder) => {
        setOrders([newOrder, ...orders]);
        fetchCustomers(1, "");
        handleShowInvoiceModal(newOrder);
    };

    const handleShowInvoiceModal = async (order) => {
        try {
            const res = await axios.get(
                `${config.backendUrl}/api/customers/${order.customer._id}`,
                { headers: { "x-auth-token": token } }
            );
            const customer = res.data;
            if (customer) {
                const updatedOrder = {
                    ...order,
                    debt_old:
                        customer?.debt -
                        (order?.totalAmount - order?.amountPaid),
                    debt: customer?.debt,
                };
                setSelectedOrderForInvoice(updatedOrder);
            } else {
                setSelectedOrderForInvoice(order);
            }
        } catch (error) {
            console.error("Error fetching customer data:", error);
            setSelectedOrderForInvoice(order);
        }
        setShowInvoiceModal(true);
    };

    const handleCancelOrder = (order) => {
        setSelectedOrder(order);
        setDialogActionType("cancel");
        setOpenConfirmDialog(true);
    };

    const handleDeleteOrder = (order) => {
        setSelectedOrder(order);
        setDialogActionType("delete");
        setOpenConfirmDialog(true);
    };

    const handleConfirmAction = async () => {
        setOpenConfirmDialog(false);
        if (!selectedOrder) return;

        try {
            if (dialogActionType === "cancel") {
                await axios.put(
                    `${config.backendUrl}/api/orders/${selectedOrder._id}/status`,
                    { status: "Cancelled" },
                    { headers: { "x-auth-token": token } }
                );
                showToast("Đơn hàng đã được hủy thành công!", "success");
            } else if (dialogActionType === "delete") {
                await axios.delete(
                    `${config.backendUrl}/api/orders/${selectedOrder._id}`,
                    { headers: { "x-auth-token": token } }
                );
                showToast("Đơn hàng đã được xóa thành công!", "success");
            }
            fetchOrders();
        } catch (error) {
            console.error("Error performing action:", error);
            showToast("Có lỗi xảy ra khi thực hiện hành động.", "error");
        } finally {
            setSelectedOrder(null);
            setDialogActionType("");
        }
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setSelectedOrder(null);
        setDialogActionType("");
    };

    const handleCloseInvoiceModal = () => {
        setShowInvoiceModal(false);
        setSelectedOrderForInvoice(null);
    };

    const handleDateRangeChange = (range) => {
        let start, end;
        switch (range) {
            case "today":
                start = startOfToday();
                end = endOfToday();
                break;
            case "thisWeek":
                start = startOfWeek(new Date(), { weekStartsOn: 1 });
                end = endOfWeek(new Date(), { weekStartsOn: 1 });
                break;
            case "thisMonth":
                start = startOfMonth(new Date());
                end = endOfMonth(new Date());
                break;
            default:
                return;
        }
        setFilterStartDate(start.toISOString());
        setFilterEndDate(end.toISOString());
    };

    useEffect(() => {
        fetchOrders();
        fetchProductsForOrder();
        fetchCustomers(1, "");
        // eslint-disable-next-line
    }, [
        searchOrderCode,
        filterProduct,
        filterStartDate,
        filterEndDate,
        filterCustomer,
        filterStatus,
        page,
        rowsPerPage,
        token,
    ]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setCustomers([]);
            fetchCustomers(1, customerSearch);
        }, 500);
        return () => clearTimeout(handler);
        // eslint-disable-next-line
    }, [customerSearch]);

    return {
        orders,
        products,
        customers,
        searchOrderCode,
        setSearchOrderCode,
        filterProduct,
        setFilterProduct,
        filterStartDate,
        setFilterStartDate,
        filterEndDate,
        setFilterEndDate,
        filterCustomer,
        setFilterCustomer,
        filterStatus,
        setFilterStatus,
        statusOptions,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        totalOrders,
        isAddOrderDialogOpen,
        setIsAddOrderDialogOpen,
        showInvoiceModal,
        handleCloseInvoiceModal,
        selectedOrderForInvoice,
        fetchOrders,
        isMobile,
        anchorEl,
        setAnchorEl,
        id,
        openPopover,
        handleCancelOrder,
        handleDeleteOrder,
        handleConfirmAction,
        handleCloseConfirmDialog,
        openConfirmDialog,
        dialogActionType,
        handleOrderAdded,
        hasPermission,
        customerSearch,
        setCustomerSearch,
        fetchCustomers,
        customerPage,
        hasMoreCustomers,
        isFetchingCustomers,
        handleShowInvoiceModal,
        handleDateRangeChange,
        hasMore,
        isLoadingMore,
        onOpenFilters,
        onCloseFilters,
    };
};

export default useOrderLogic;